/**
 * frontend/src/views/PreferencesView.ts
 * =======================================
 * Modal de configuración de preferencias del usuario.
 *
 * Se abre desde el botón ⚙️ del header (visible en todas las vistas).
 * Las preferencias se persisten en localStorage via utils/preferences.ts.
 *
 * Diseño para el futuro:
 *   - Las preferencias marcadas como "Próximamente" se almacenan ya en el
 *     modelo de datos (Preferences en types.ts) pero el campo de UI está
 *     deshabilitado hasta que se implemente Drive OAuth 2.0.
 *   - Cuando se implemente Login + BD propia, savePreferences() se reemplaza
 *     por un PUT /api/preferencias — cambio acotado a utils/preferences.ts.
 *
 * Secciones:
 *   A. Reportes       — nombre del archivo de salida, nombre de la empresa
 *   B. Notificaciones — emails destinatarios
 *   C. Motor          — umbral de diferencia ignorable (la tolerancia vive en el perfil)
 *   D. Cuenta contable — código de cuenta del libro mayor
 *   E. Google Drive   — carpetas de entrada/salida (próximamente)
 */

import type { Preferences } from "../types";

import { escapeHtml as _esc } from "../utils/texto";
import { iconoAyuda, prepararDialogo } from "../utils/ui";
import { icon } from "../utils/icons";
import { direccionesInvalidas } from "../utils/destinatarios";

export interface OpcionesPreferencias {
  /** Abre con el cursor en los destinatarios: se llega desde «sin destinatarios». */
  enfocarDestinatarios?: boolean;
}

export function openPreferencesModal(
  current: Preferences,
  onSave: (updated: Preferences) => void,
  opciones: OpcionesPreferencias = {},
): void {
  // ── Overlay ────────────────────────────────────────────────────────────────
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "prefs-title");

  overlay.innerHTML = `
    <div class="modal-panel modal-panel--prefs">

      <div class="modal-header">
        <span class="modal-header__icon">${icon("settings")}</span>
        <h2 class="modal-header__title" id="prefs-title">Preferencias</h2>
        <button class="modal-close" type="button" aria-label="Cerrar preferencias">${icon("close")}</button>
      </div>

      <div class="modal-body">

        <!-- A. Reportes -->
        <fieldset class="prefs-section">
          <legend class="prefs-section__title">${icon("file")} Reportes</legend>

          <div class="prefs-field" data-recorrido="empresa">
            <label class="prefs-label" for="pref-nombre-empresa">
              Nombre de la empresa
              ${iconoAyuda("Se incluye en el encabezado del Excel y del PDF.")}
            </label>
            <input class="prefs-input" id="pref-nombre-empresa" type="text"
              placeholder="Ej: Empresa S.A."
              value="${_esc(current.nombreEmpresa)}" />
          </div>

          <div class="prefs-field" data-recorrido="nombre-archivo">
            <label class="prefs-label" for="pref-nombre-archivo">
              Nombre del archivo
              ${iconoAyuda(`
                Variables disponibles:
                <br><br>
                <b>{cuenta}</b> — código de cuenta del perfil del libro mayor<br>
                <b>{periodo}</b> — período conciliado (ej: jun_2026)<br>
                <b>{fecha}</b> — fecha de generación (AAAAMMDD)
                <br><br>
                Ejemplo: <b>Conciliacion_{cuenta}_{periodo}</b> produce
                <i>Conciliacion_1.1.1.05_jun_2026.xlsx</i>`)}
            </label>
            <input class="prefs-input" id="pref-nombre-archivo" type="text"
              placeholder="Conciliacion_Bancaria_{cuenta}_{periodo}"
              value="${_esc(current.nombreArchivoTemplate)}" />
          </div>
        </fieldset>

        <!-- B. Notificaciones -->
        <fieldset class="prefs-section">
          <legend class="prefs-section__title">${icon("mail")} Notificaciones</legend>

          <div class="prefs-field" data-recorrido="destinatarios">
            <label class="prefs-label" for="pref-emails">
              Destinatarios del reporte
              ${iconoAyuda("Separados por comas. Reciben el Excel y el PDF cuando se elige enviar el reporte. Sin destinatarios, el resultado se revisa y se descarga, pero no se envía.")}
            </label>
            <input class="prefs-input" id="pref-emails" type="text"
              inputmode="email" autocomplete="email"
              aria-describedby="pref-emails-error"
              placeholder="contador@empresa.com, gerencia@empresa.com"
              value="${_esc(current.emailDestinatarios)}" />
            <p class="prefs-error" id="pref-emails-error" role="alert" hidden></p>
          </div>
        </fieldset>

        <!-- C. Motor de conciliación -->
        <fieldset class="prefs-section">
          <legend class="prefs-section__title">${icon("mapping")} Conciliación</legend>

          <!-- La tolerancia de fechas se configura por perfil de mapeo
               (wizard, paso 2): cada cuenta tolera corrimientos distintos. -->

          <div class="prefs-field" data-recorrido="umbral">
            <label class="prefs-label" for="pref-umbral">
              Umbral de diferencia ignorable
              ${iconoAyuda(`
                Las diferencias de saldo menores o iguales a este monto se
                consideran dentro del margen aceptable y no se reportan como
                diferencia.
                <br><br>
                <b>0</b> — desactivado: cualquier diferencia se reporta.`)}
            </label>
            <input class="prefs-input prefs-input--short" id="pref-umbral" type="number"
              min="0" step="0.01"
              value="${current.umbralDiferenciaIgnorable}" />
          </div>
        </fieldset>

        <!-- D. Cuenta contable → movida al perfil de mapeo -->
        <!-- El código de cuenta ahora se configura en el perfil de mapeo del Libro Mayor (wizard, paso 2). -->

        <!-- E. Google Drive (futuro) -->
        <fieldset class="prefs-section prefs-section--future">
          <legend class="prefs-section__title">
            ${icon("cloud")} Google Drive
            <span class="prefs-badge prefs-badge--soon">Próximamente</span>
          </legend>

          <div class="prefs-field">
            <label class="prefs-label" for="pref-drive-in">Carpeta de entrada (extractos y libros)</label>
            <input class="prefs-input" id="pref-drive-in" type="text"
              placeholder="Se configurará al conectar con Google Drive"
              value="${_esc(current.driveFolderInputNombre)}"
              disabled />
          </div>

          <div class="prefs-field">
            <label class="prefs-label" for="pref-drive-out">Carpeta de salida (reportes generados)</label>
            <input class="prefs-input" id="pref-drive-out" type="text"
              placeholder="Se configurará al conectar con Google Drive"
              value="${_esc(current.driveFolderOutputNombre)}"
              disabled />
          </div>
        </fieldset>

      </div>

      <div class="modal-footer modal-footer--prefs">
        <button class="btn btn--ghost" id="btn-prefs-cancel" type="button">Cancelar</button>
        <button class="btn btn--primary" id="btn-prefs-save" type="button">Guardar preferencias</button>
      </div>

    </div>
  `;

  document.body.appendChild(overlay);

  const campoEmails = overlay.querySelector<HTMLInputElement>("#pref-emails")!;
  const errorEmails = overlay.querySelector<HTMLElement>("#pref-emails-error")!;

  function marcarEmails(problema: string | null): void {
    errorEmails.textContent = problema ?? "";
    errorEmails.hidden = !problema;
    campoEmails.toggleAttribute("aria-invalid", Boolean(problema));
  }
  campoEmails.addEventListener("input", () => marcarEmails(null));

  // ── Event handlers ────────────────────────────────────────────────────────
  function closeModal(): void {
    overlay.remove();
  }
  prepararDialogo(overlay, closeModal);

  if (opciones.enfocarDestinatarios) {
    // Después del foco inicial del diálogo, que va al primer campo.
    requestAnimationFrame(() => {
      campoEmails.scrollIntoView({ block: "center", behavior: "smooth" });
      campoEmails.focus({ preventScroll: true });
    });
  }

  overlay.querySelector(".modal-close")!.addEventListener("click", closeModal);
  overlay.querySelector("#btn-prefs-cancel")!.addEventListener("click", closeModal);

  // Cerrar al hacer click en el backdrop (fuera del panel)
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeModal();
  });

  // Guardar
  overlay.querySelector("#btn-prefs-save")!.addEventListener("click", () => {
    // Vacío se puede guardar: los destinatarios hacen falta para enviar el
    // reporte, no para conciliar, y se piden al enviarlo. Lo que no es una
    // dirección, no.
    const emails = _val(overlay, "#pref-emails");
    const problema = direccionesInvalidas(emails);
    if (problema) {
      marcarEmails(problema);
      campoEmails.focus();
      return;
    }

    const updated: Preferences = {
      nombreEmpresa: _val(overlay, "#pref-nombre-empresa"),
      nombreArchivoTemplate: _val(overlay, "#pref-nombre-archivo") || "Conciliacion_Bancaria_{cuenta}_{periodo}",
      emailDestinatarios: emails,
      // La tolerancia migró al perfil de mapeo; se conserva el valor previo
      // solo para no romper preferencias ya guardadas en localStorage.
      toleranciaFechasDias: current.toleranciaFechasDias,
      umbralDiferenciaIgnorable: parseFloat(_val(overlay, "#pref-umbral")) || 0,
      // codigoCuentaLibroMayor → ahora se configura en el perfil de mapeo del Libro Mayor
      codigoCuentaLibroMayor: current.codigoCuentaLibroMayor,
      // Drive (disabled inputs — preserve current values)
      driveFolderInputNombre: current.driveFolderInputNombre,
      driveFolderOutputNombre: current.driveFolderOutputNombre,
    };
    onSave(updated);
    closeModal();

    // Feedback visual breve
    _showSavedToast();
  });

}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _val(root: Element, selector: string): string {
  return (root.querySelector<HTMLInputElement | HTMLSelectElement>(selector)?.value ?? "").trim();
}


function _showSavedToast(): void {
  const toast = document.createElement("div");
  toast.className = "toast toast--success";
  toast.innerHTML = `${icon("check")} Preferencias guardadas`;
  toast.setAttribute("role", "status");
  document.body.appendChild(toast);
  setTimeout(() => toast.classList.add("toast--visible"), 10);
  setTimeout(() => {
    toast.classList.remove("toast--visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}
